import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, MbsSize, ModalService } from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { addNew } from 'mbs-ui-shared@models/SidePanelUriParams';
import { filter, mergeMap, noop, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { OnboardingMode } from 'wizards/services/onboarding/onboarding.constants';
import { OnboardingService } from 'wizards/services/onboarding/onboarding.service';

@UntilDestroy()
@Component({
  selector: 'mbs-add-data-or-run-gsw-hint',
  templateUrl: './add-data-or-run-gsw-hint.html',
  styles: ['.mbs-no-computers { &-tile { height: 64px; } }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ButtonComponent]
})
export class AddDataOrRunGSWHintComponent implements OnInit {
  private http = inject(HttpClient);
  private auth = inject(AuthFacade);
  private ability = inject(AbilityService);
  private i18N = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private onboardingService = inject(OnboardingService);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  @Input() aspect: 'computers' | 'domains' = 'computers';

  public elementSelectors = {
    id: {
      runGSW: 'add-data-or-run-gsw-hint_run_gsw'
    }
  };

  public isReturnToGswAvailable = false;
  public loading = true;
  public loadingAspect = false;

  public get title(): string {
    return this.i18N.transform(this.aspect === 'computers' ? 'shared:computers.noComputersTitle' : 'shared:domains.noDomainsTitle');
  }

  public get description(): string {
    if (this.aspect === 'computers') {
      const returnToGSWText = this.isReturnToGswAvailable ? `\n${this.i18N.transform('shared:computers.returnToGSW')}` : '';

      return this.i18N.transform('shared:computers.noComputersDescription') + returnToGSWText;
    }

    return this.i18N.transform(
      this.isReturnToGswAvailable ? 'shared:domains.noDomainsDescriptionWithGSW' : 'shared:domains.noDomainsDescription'
    );
  }

  public get addButtonText(): string {
    if (this.aspect === 'computers') {
      return this.i18N.transform('shared:computers.addComputer', { format: 'title' });
    }

    return this.i18N.transform('shared:domains.addDomain', { format: 'title' });
  }

  public get runGSWButtonText(): string {
    return this.i18N.transform(`shared:${this.aspect}.gettingStartedWizard`, { format: 'title' });
  }

  private mode: OnboardingMode;

  ngOnInit(): void {
    this.calculateAddingMode(this.router.url);

    this.auth.currentUser$
      .pipe(
        mergeMap((admin) => {
          const modeReq$ = admin?.IsProvider ? this.onboardingService.mode() : of({ mode: null as OnboardingMode });

          return modeReq$.pipe(map(({ mode }) => ({ admin, mode })));
        })
      )
      .subscribe(({ admin, mode }) => {
        const isSpecificReseller = this.ability.can('read', PermissionsEnum.B2Only) || this.ability.can('read', 'IDriveOnly');
        this.isReturnToGswAvailable =
          (!admin?.ResellerInfo || isSpecificReseller) && !this.ability.can('read', 'SubAdmin') && mode !== OnboardingMode.RmmScripts;

        this.loading = false;
        this.mode = this.isReturnToGswAvailable ? mode : null;
        this.cdr.detectChanges();
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.calculateAddingMode(event.urlAfterRedirects));
  }

  public onAddDataButtonClick(): void {
    this.aspect === 'computers' ? this.addComputer() : this.addDomain();
  }

  public goToGettingStarted() {
    this.loadingAspect = true;

    if (this.mode === OnboardingMode.RmmScripts) {
      this.router.navigateByUrl(RoutingPath.ApGettingStartTryRMM);
    } else {
      this.http
        .post('/api/gettingstarted/restart', {})
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          if (this.aspect === 'domains') {
            localStorage.setItem('onboardingFlow', 'apps');
            localStorage.setItem('onboardingStep', 'configureBackup');
          }

          location.href = RoutingPath.ApGettingStart;
        });
    }
  }

  private calculateAddingMode(url: string): void {
    if (this.aspect === 'computers') {
      return;
    }

    if (url === RoutingPath.ApAppsDomains) {
      this.loadingAspect = false;
    }

    if (url === RoutingPath.ApAppsDomains + '/' + addNew) {
      this.loadingAspect = true;
    }

    this.cdr.detectChanges();
  }

  private addComputer() {
    this.loadingAspect = true;

    import('mbs-ui-shared@components/add-computer/add-computer.component').then(({ AddComputerComponent }) => {
      const modal = this.modalService.openRef(AddComputerComponent, {
        size: MbsSize.lg
      });

      modal.componentInstance.mode = AgentType.Backup;
      modal.componentInstance.showPendingFilterButton = false;

      modal.result.finally(noop);
      this.loadingAspect = false;
    });
  }

  private addDomain() {
    this.loadingAspect = true;

    this.router
      .navigate([RoutingPath.ApAppsDomains, addNew], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        queryParams: {}
      })
      .then(noop);
  }
}
